import { SaleDetailModel } from 'api-hooks/sale/model';
import { useRescheduleTicketSale } from 'api-hooks/sale/ticket-sale';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useNavigation from 'hooks/use-navigation';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import {
  FormContent,
  FormDivider,
  HalfContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { FlightRescheduleFormType, FlightRescheduleSchema } from './form-type';
import useGetDefaultValues from './hooks/use-get-default-values';
import FlightItems from './items';
import SummaryFlightReschedule from './summary';
import { totalFlightRescheduleCalculation } from './total-calculation';
import RescheduleHistoryTable from '../reschedule-history-table';

interface Props {
  flightSale: SaleDetailModel;
}

export default function FlightRescheduleForm(props: Props) {
  const { t } = useTranslation();
  const { flightSale } = props;
  const { navigate, close } = useNavigation();

  const { mutateAsync } = useRescheduleTicketSale();

  const defaultValues = useGetDefaultValues({ flightSale });

  const methods = useForm<FlightRescheduleFormType>({
    defaultValues,
    resolver: useYupValidationResolver(FlightRescheduleSchema),
    mode: 'onChange',
  });

  const onCancel = () => {
    close();
    navigate(NavigationRoutes.TicketSalesView, {
      id: flightSale.id,
    });
  };

  const onSubmit = async (values: FlightRescheduleFormType) => {
    try {
      values.total = totalFlightRescheduleCalculation(values.flightSales);

      //if error then it's able to resubmit
      const flightSales = values.flightSales
        .filter((curr) =>
          curr.flightSaleGuests.some((curfsg) => curfsg.isSelected),
        )
        .map((fs) => ({
          ...fs,
          isSelected: undefined,
          flightSaleGuests: fs.flightSaleGuests
            .filter((fsg) => fsg.isSelected)
            .map((fsg) => ({ ...fsg, isSelected: undefined })),
          flightSaleAddOns: fs.flightSaleAddOns
            .filter((fsg) => fsg.isSelected)
            .map((fsg) => ({ ...fsg, isSelected: undefined })),
        }));

      if (!flightSales.length) {
        notification.error({ message: t('error:at_least_select_one_item') });
        return;
      }

      await mutateAsync({
        flightSaleId: flightSale.id,
        body: { ...values, flightSales },
      });
      onCancel();
    } catch (e) {
      notification.error({ message: e.message });
    }
  };

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      noReset
    >
      <FormContent>
        <FormHeader
          title={t('reschedule:reschedule_flight')}
          data={flightSale}
          onCancel={onCancel}
          noDelete
        />
        <div>
          <SectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="invoiceNumber"
                label={t('common:invoice_number')}
                disabled
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text"
                name="customer"
                label={t('common:customer')}
                disabled
              />
            </HalfContainer>
          </SectionContainer>
          <HalfContainer>
            <Input
              type="date-time"
              name="transactionAt"
              label={t('common:transaction_at')}
              clearable={false}
              required
            />
          </HalfContainer>
          <Separator gap={32} direction="vertical">
            <RescheduleHistoryTable type="flight" saleId={flightSale.id} />
          </Separator>

          <Separator gap={16} direction="vertical">
            <FlightItems />
          </Separator>
          <FormDivider />
        </div>
      </FormContent>
      <SummaryFlightReschedule />
    </Form>
  );
}
